/**
 * Utility to bridge UI events to both the Parent Window and the Backend Telemetry
 */
import React from 'react';
import { buildExternalAiUrl } from './apiConfig';
import { useGlobalAuthContext } from "../context/AuthContext";
import { jwtDecode } from "jwt-decode";
import { buildClientAttributionDetail } from './eventAttribution';
import { getGuestSessionId } from './guestSession';

export const ANALYTICS_ENDPOINT = buildExternalAiUrl('/api/v2/analytics');

/**
 * Logs an event to the backend and notifies the parent window.
 * * @param {string} category - e.g., 'segmentation', 'editor', 'ui'
 * @param {string} eventName - e.g., 'image_upload', 'room_created'
 * @param {Object} payload - Flexible detail object (detail in Schema)
 * @param {Object} options - Additional meta like user or customer_ref
 */
export const trackEvent = async (_window, category, eventName, payload = {}, options = {}) => {
  // 1. Notify Parent Window (Existing Logic)
  if (_window && _window !== window) {
    _window.postMessage({
      type: category,
      payload: {event: eventName, ...payload},
    }, '*');
  }

  // 2. Prepare Mongoose-compatible EventLog object
  const guestSessionId = getGuestSessionId();
  // For account_created events FE knows the new user's email/name at log
  // time (passed in `payload`). Mirror those onto `meta` to match BE PR #96's
  // server-emitted shape so the signup drill-down can resolve identity from
  // the event payload directly — no `/user/get-all` round-trip required.
  // No-op for any other event since `payload.email` is rarely set elsewhere.
  const isAccountCreated = eventName === 'account_created';
  const metaEmail =
    isAccountCreated && typeof payload?.email === 'string' && payload.email.trim()
      ? payload.email.trim()
      : null;
  const metaName =
    isAccountCreated && typeof payload?.name === 'string' && payload.name.trim()
      ? payload.name.trim()
      : null;
  const attributedDetail = buildClientAttributionDetail(eventName, payload, {
    userEmail: options.userEmail,
  });
  const eventLog = {
    meta: {
      category: category,
      event: eventName,
      user: options.userEmail || window.__USER_ID__ || 'guest',
      customer_ref: options.customer_ref || 'default-not-provided',
      origin: window.location.hostname,
      ...(guestSessionId ? { guestSessionId } : {}),
      ...(metaEmail ? { email: metaEmail } : {}),
      ...(metaName ? { name: metaName } : {}),
    },
    detail: attributedDetail,
  };

  // 3. Post to REST Endpoint
  try {
    const response = await fetch(ANALYTICS_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(eventLog),
      // We use 'keepalive' to ensure the request completes even if the page unloads
      keepalive: true,
    });

    if (!response.ok) {
      console.warn('[Analytics] Failed to log event:', response.statusText);
    }
  } catch (err) {
    console.error('[Analytics] Network error:', err);
  }
};

/**
 * React Hook for easier usage within components
 */
export const useAnalytics = (customerRef) => {
  const { currentUserSessionStorage } = useGlobalAuthContext();
  const userEmail = React.useMemo(() => {
    if (!currentUserSessionStorage) return null;
    try {
      return jwtDecode(currentUserSessionStorage)?.user?.email ?? null;
    } catch {
      return null;
    }
  }, [currentUserSessionStorage]);
  return {
    eventLog: (_window, category, event, payload) =>
      trackEvent(_window, category, event, payload, {
        customer_ref: customerRef,
        userEmail,
      }),
  };
};